import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { figurasService, type FiguraEmpresa } from '@/services/figurasApi';

export const FIGURA_UPLOAD_ACCEPT = 'image/jpeg,image/png,image/webp';
const FIGURA_UPLOAD_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);
const FIGURA_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Biblioteca de figuras da empresa (2026-10-23), usada no catálogo de figuras do organograma e do fluxograma:
 * lista as imagens enviadas, envia novas (ficam para toda a empresa) e remove (admin ou quem enviou). Remover só
 * tira do catálogo — os desenhos que já usam a imagem continuam mostrando.
 */
export function BibliotecaFiguras({
  empresaId,
  isSelecionada,
  onSelect,
  disabled = false,
}: {
  empresaId?: string | null;
  /** Diz se a figura é a que está em uso (destaca no grid). */
  isSelecionada?: (figura: FiguraEmpresa) => boolean;
  onSelect: (figura: FiguraEmpresa) => void;
  disabled?: boolean;
}) {
  const { user } = useAuth();
  const [figuras, setFiguras] = useState<FiguraEmpresa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [confirmarRemocao, setConfirmarRemocao] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    figurasService.listar(empresaId).then((res) => {
      if (!ativo) return;
      setFiguras(res.success ? res.data || [] : []);
      setCarregando(false);
    });
    return () => { ativo = false; };
  }, [empresaId]);

  const podeRemover = (figura: FiguraEmpresa) =>
    user?.tipo === 'admin' || user?.tipo === 'superadmin' || figura.criado_por === user?.id;

  const enviar = async (file: File | undefined) => {
    if (!file) return;
    if (!FIGURA_UPLOAD_MIME.has(file.type)) {
      toast.error('Formato não suportado. Envie JPEG, PNG ou WEBP.');
      return;
    }
    if (file.size > FIGURA_UPLOAD_MAX_BYTES) {
      toast.error('Imagem muito grande (máximo 5 MB).');
      return;
    }
    setEnviando(true);
    const res = await figurasService.enviar(file, empresaId);
    setEnviando(false);
    if (!res.success || !res.data) {
      toast.error(res.error || 'Erro ao enviar imagem');
      return;
    }
    const nova = res.data;
    setFiguras((atual) => [nova, ...atual]);
    toast.success('Imagem adicionada às figuras da empresa');
    onSelect(nova);
  };

  const remover = async (figura: FiguraEmpresa) => {
    setConfirmarRemocao(null);
    const res = await figurasService.remover(figura.id, empresaId);
    if (!res.success) {
      toast.error(res.error || 'Erro ao remover figura');
      return;
    }
    setFiguras((atual) => atual.filter((f) => f.id !== figura.id));
    toast.success('Figura removida do catálogo (os desenhos que já a usam continuam iguais)');
  };

  return (
    <div className="space-y-2 rounded-2xl border border-dashed border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-foreground">Figuras da empresa</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          disabled={disabled || enviando}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus className="h-3.5 w-3.5" />
          {enviando ? 'Enviando...' : 'Enviar imagem'}
        </Button>
      </div>

      {carregando ? (
        <p className="py-2 text-center text-[11px] text-muted-foreground">Carregando...</p>
      ) : figuras.length === 0 ? (
        <p className="py-2 text-center text-[11px] text-muted-foreground">
          Nenhuma imagem ainda. As que você enviar ficam disponíveis para toda a empresa.
        </p>
      ) : (
        <div className="grid max-h-56 grid-cols-3 gap-2 overflow-y-auto">
          {figuras.map((figura) => (
            <div key={figura.id} className="group relative">
              <button
                type="button"
                disabled={disabled}
                title={figura.nome}
                onClick={() => onSelect(figura)}
                className={cn(
                  'flex w-full flex-col items-center gap-1 overflow-hidden rounded-xl border border-border bg-background px-1.5 py-2 text-[11px] text-muted-foreground shadow-sm transition hover:border-primary/40 hover:text-foreground',
                  isSelecionada?.(figura) && 'border-primary ring-2 ring-primary/20 text-foreground',
                )}
              >
                <img src={figura.url} alt={figura.nome} className="h-8 w-8 shrink-0 object-contain" />
                <span className="w-full truncate text-center">{figura.nome}</span>
              </button>
              {!disabled && podeRemover(figura) && (
                confirmarRemocao === figura.id ? (
                  <button
                    type="button"
                    onClick={() => void remover(figura)}
                    onBlur={() => setConfirmarRemocao(null)}
                    className="absolute inset-x-1 bottom-1 rounded-md bg-destructive px-1 py-0.5 text-[10px] font-medium text-destructive-foreground"
                    autoFocus
                  >
                    Remover?
                  </button>
                ) : (
                  <button
                    type="button"
                    title="Remover do catálogo da empresa"
                    aria-label={`Remover ${figura.nome} do catálogo`}
                    onClick={() => setConfirmarRemocao(figura.id)}
                    className="absolute right-1 top-1 hidden rounded-md bg-background/90 p-0.5 text-muted-foreground shadow-sm hover:text-destructive group-hover:block focus:block"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">JPEG, PNG ou WEBP, até 5 MB. Fica disponível para toda a empresa.</p>
      <input
        ref={inputRef}
        type="file"
        accept={FIGURA_UPLOAD_ACCEPT}
        className="hidden"
        onChange={(event) => {
          void enviar(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </div>
  );
}
